import { createTheme, ThemeProvider } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion, AccordionDetails, AccordionSummary,
  AppBar,
  Autocomplete,
  Box,
  Button,
  Dialog, DialogActions,
  FormControl,
  Grid,
  IconButton,
  Paper,
  Slide,
  TextField,
  Toolbar,
  Typography
} from '@mui/material';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import ColorConstants from '../../defaults/ColorConstants';
import FieldNames from '../../defaults/FieldNames.json';
import GridDefinitions from '../../defaults/GridDefinitions.json';
import AuditLogService from '../../services/AuditLogService';
import ErrorService from '../../services/ErrorService';
import IDService from '../../services/IDService';
import UserService from '../../services/UserService';
import { getSuccessfulNewRoles, getUpdatedUserInfo, getUserRoles } from '../../utils/AdminHelper';
import {formatDateForDisplay, getSaveableDate} from '../../utils/DateHelper';
import { resetFuelsCacheValuesToPostFire, resetFuelsCustomFiltersToPostFire, resetPostFireCacheValuesToFuels, resetPostFireCustomFiltersToFuels, resetProjectCacheValues } from '../../utils/ReduxHelper';
import AdminDialogAssignment from './AdminDialogAssignment';
import AdminUserAssignment from './AdminUserAssignment';
import _ from "lodash";

const useStyles = makeStyles({
  underline: {
    "&&&:before": {
      borderBottom: "2px solid red"
    },
    "&&:after": {
      borderBottom: "2px solid red"
    }
  },
  condensedFormControl: {
    margin: "5px 15px 5px 15px",
    minWidth: 120,
    width: '90%'
  },
  formControl: {
    margin: 15,
    minWidth: 120,
    width: '90%'
  },
  snackBarStyle: {
    top: '120px !important'
  },
});

const theme = createTheme({
  overrides: {
    MuiPaper: {
      root: {
        backgroundColor: 'green'
      }
    },
    MuiOutlinedInput: {
      root: {
        '& $notchedOutline': {
          //initial border
          borderColor: ColorConstants.BlueAlien,
        },
        '&:hover $notchedOutline': {
          //border on hover
          borderColor: ColorConstants.BlueAlien,
          border: '2px solid'
        },
        '&$focused $notchedOutline': {
          //border on focus
          borderColor: ColorConstants.BlueAlien,
          border: '2px solid'
        },
      },
    }
  },
});

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function AdminDialog({open, onClose, currentUserInfo, setCurrentUserInfo, setCurrentUserRoleInfo}) {
  const [roleValues] = useState([{name: FieldNames.roles.local, code: FieldNames.roles.local}, {name: FieldNames.roles.region, code: FieldNames.roles.region}, {name: FieldNames.roles.agency, code: FieldNames.roles.agency}, {name: FieldNames.roles.department, code: FieldNames.roles.department}]);
  const [allUsers, setAllUsers] = useState([]);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [nifcUsername, setNifcUsername] = useState('');
  const [userIsAdmin, setUserIsAdmin] = useState(FieldNames.yesNo.no);
  const [userIsActive, setUserIsActive] = useState(FieldNames.yesNo.yes);
  const [isTribalUser, setIsTribalUser] = useState(FieldNames.yesNo.no);
  const [userIsFuels, setUserIsFuels] = useState(FieldNames.yesNo.yes);
  const [userIsPostFire, setUserIsPostFire] = useState(FieldNames.yesNo.no);
  const [userDefaultApp, setUserDefaultApp] = useState("");
  const [createdBy,setCreatedBy] = useState(''); 
  const [createdOn,setCreatedOn] = useState(''); //getSaveableDate(new Date());
  const [modifiedBy,setModifiedBy] = useState('');
  const [modifiedOn,setModifiedOn] = useState(''); //getSaveableDate(new Date());
  const [selectedUserOriginal, setSelectedUserOriginal] = useState({});
  const [firstNameUpdate, setFirstNameUpdate] = useState('');
  const [lastNameUpdate, setLastNameUpdate] = useState('');
  const [emailUpdate, setEmailUpdate] = useState('');
  const [nifcUsernameUpdate, setNifcUsernameUpdate] = useState('');
  const [userIsAdminUpdate, setUserIsAdminUpdate] = useState(FieldNames.yesNo.no);
  const [userIsActiveUpdate, setUserIsActiveUpdate] = useState(FieldNames.yesNo.no);
  const [userIsFuelsUpdate, setUserIsFuelsUpdate] = useState(FieldNames.yesNo.no);
  const [userIsPostFireUpdate, setUserIsPostFireUpdate] = useState(FieldNames.yesNo.no);
  const [userDefaultAppUpdate, setUserDefaultAppUpdate] = useState("");
  const [isTribalUserUpdate, setIsTribalUserUpdate] = useState(FieldNames.yesNo.no);
  const [idVal,setIdVal] = useState(null);
  const [userObjectIDUpdate, setUserObjectIDUpdate] = useState(null);
  const [userIDUpdate, setUserIDUpdate] = useState("");
  const [roleUpdate, setRoleUpdate] = useState(null);
  const [role, setRole] = useState(null);
  const [isNifcAlreadyUsed, setIsNifcAlreadyUsed] = useState(false);
  const [nifcInputValue, setNifcInputValue] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [isFailure, setIsFailure] = useState(false);
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedRoleUpdate, setSelectedRoleUpdate] = useState("");
  const [departmentSelection, setDepartmentSelection] = useState({Department: FieldNames.defaults.department});
  const [departmentSelectionUpdate, setDepartmentSelectionUpdate] = useState(null);
  const [agencySelection, setAgencySelection] = useState({Agency: ""});
  const [agencySelectionUpdate, setAgencySelectionUpdate] = useState(null);
  const [regionSelection, setRegionSelection] = useState([]);
  const [regionSelectionUpdate, setRegionSelectionUpdate] = useState([]);
  const [unitSelection, setUnitSelection] = useState([]);
  const [unitSelectionUpdate, setUnitSelectionUpdate] = useState([]);
  const [tribeSelection, setTribeSelection] = useState({TribeFullName: ""});
  const [tribeSelectionUpdate, setTribeSelectionUpdate] = useState({TribeFullName: ""});
  const [subUnitSelection, setSubUnitSelection] = useState([]);
  const [subUnitSelectionUpdate, setSubUnitSelectionUpdate] = useState([]);
  const [currentlyUsedRegions, setCurrentlyUsedRegions] = useState([]);
  const [currentlyUsedRegionsUpdate, setCurrentlyUsedRegionsUpdate] = useState([]);
  const [currentlyUsedUnits, setCurrentlyUsedUnits] = useState([]);
  const [currentlyUsedUnitsUpdate, setCurrentlyUsedUnitsUpdate] = useState([]);
  const [dbSavedUserRoles, setDbSavedUserRoles] = useState([]);
  const [userHistoryRows,setUserHistoryRows] = useState([]);
  const [userHistoryCols,setUserHistoryCols] = useState([]);
  const config = useSelector((state) => state.configuration.config);
  const rootServiceUrl = useSelector((state) => state.configuration.rootServiceUrl);
  const nifcToken = useSelector((state) => state.configuration.nifcToken);
  const [isUserDirty, setIsUserDirty] = useState(false);
  const [isUserRoleDirty, setIsUserRoleDirty] = useState(false);

  const userAuditLogGridColDefinitions = [
    GridDefinitions.UserAuditLog.modifiedBy,
    GridDefinitions.UserAuditLog.modifiedOn,
    GridDefinitions.UserAuditLog.modification
  ];

  const classes = useStyles();

  useEffect(() => {
    
    if(open){
      handleClearCreateUser();
      handleClearUpdateUser();
      establishGridColumns();
      UserService.getAllUsers(rootServiceUrl, config).then(response => {
        setAllUsers(response);
      }).catch(error => {
        ErrorService.addErrorLogEntry(currentUserInfo[FieldNames.userFields.nifcUsername], error);
      });
    }
  }, [open, config, rootServiceUrl]);

  const handleSaveCreateUser = () => {
    const newUserID = uuidv4();

    if(nifcUsername){
      let currDate = getSaveableDate(new Date());
      const userInfo = {
        [FieldNames.userFields.firstName]: firstName,
        [FieldNames.userFields.lastName]: lastName,
        [FieldNames.userFields.email]: email,
        [FieldNames.userFields.nifcUsername]: nifcUsername,
        [FieldNames.userFields.isActive]: FieldNames.yesNo.yes,
        [FieldNames.userFields.isAdmin]: userIsAdmin,
        [FieldNames.userFields.isFuels]: userIsFuels,
        [FieldNames.userFields.isPostFire]: userIsPostFire,
        [FieldNames.userFields.defaultApp]: userDefaultApp,
        [FieldNames.userFields.userID]: newUserID,
        [FieldNames.userFields.isTribalUser]: isTribalUser,
        [FieldNames.userFields.createdBy]: currentUserInfo[FieldNames.userFields.nifcUsername],
        [FieldNames.userFields.createdOnDate]: currDate,
        [FieldNames.userFields.lastModifiedBy]: currentUserInfo[FieldNames.userFields.nifcUsername],
        [FieldNames.userFields.lastModifiedDate]: currDate
      }

      UserService.addUser(rootServiceUrl, nifcToken, userInfo, config).then(response => {
        if(response && response.addResults && response.addResults[0].success){
          IDService.getUserID(response.addResults[0].objectId).then(idVal => {
            savingUserRoles(false, idVal, newUserID, null, false, true, true);
            UserService.getAllUsers(rootServiceUrl, config).then(response => {
              var newUser = response.filter((user) => user[FieldNames.userFields.userID] === newUserID);
              if(newUser)
              {
                AuditLogService.addAuditLogEntry(
                  newUser[0], newUser[0][FieldNames.userFields.id], FieldNames.auditLogJoinTypes.users, currentUserInfo[FieldNames.userFields.nifcUsername],
                  ["User was created"]).then(response => { }).catch(error => {
                    ErrorService.addErrorLogEntry(currentUserInfo[FieldNames.userFields.nifcUsername], error);
                  });
              }
              setAllUsers(response);
            }).catch(error => {
              ErrorService.addErrorLogEntry(currentUserInfo[FieldNames.userFields.nifcUsername], error);
            });
          })
        } else {
          setIsFailure(true);
          setSnackbarMessage("User create failed!");
          setSnackbarOpen(true);
        }
      }).catch(error => {
        ErrorService.addErrorLogEntry(currentUserInfo[FieldNames.userFields.nifcUsername], error);
      });
    } 
  }

  const getUserAuditLog = (userJoinID) => {
    if(userJoinID){
      AuditLogService.getEntityAuditLog(FieldNames.auditLogJoinTypes.users, userJoinID).then(userAuditRows => {
        if(userAuditRows && userAuditRows.length > 0){
          var mappedRows = userAuditRows.map((auditRow) => {
            try{
              return {id: auditRow.OBJECTID, ModifiedBy: auditRow.ModifiedBy, ModifiedDate: formatDateForDisplay(auditRow.ModifiedDate),
                    Modification: JSON.parse(auditRow.AuditEntryJson)[0]}
            } catch(e){
              return {id: auditRow.OBJECTID, ModifiedBy: auditRow.ModifiedBy, ModifiedDate: formatDateForDisplay(auditRow.ModifiedDate),
                Modification: auditRow.AuditEntryJson}
            }
          })
          setUserHistoryRows(mappedRows);
        } else {
          setUserHistoryRows([]);
        }
      }).catch(error => {
        ErrorService.addErrorLogEntry(currentUserInfo[FieldNames.userFields.nifcUsername], error);
      });
    } 
  }

  const establishGridColumns = () => {
    let columns = [];
    userAuditLogGridColDefinitions.forEach((colDef) => {
      columns.push({
        field: colDef.value,
        headerName: colDef.header,
        type: colDef.type,
        hideable: colDef.hideable,
        sortable: colDef.sortable,
        editable: colDef.editable,
        flex: colDef.flex,
        align: colDef.align,
        disableExport: colDef.disableExport,
        valueGetter: (value) => {
          return value; // Used for formatting string values I.E. currency formatting
        },
      });
    });


    setUserHistoryCols(columns);
  };

  const addAuditLog = (isUserDirty, isUserSuccess, isUserRoleDirty, isUserRoleSuccess) => {
    const user = {
      [FieldNames.userFields.id]: idVal,
      [FieldNames.featureFields.objectID]: userObjectIDUpdate
    }
    if(isUserDirty && isUserSuccess && isUserRoleDirty && isUserRoleSuccess){
      //Add audit log entry - not stack so that even if this call fails the others will continue
      AuditLogService.addAuditLogEntry( 
        user, idVal, FieldNames.auditLogJoinTypes.users, currentUserInfo[FieldNames.userFields.nifcUsername],
        ["User was modified", "User Role was modified"]).then(response => {
          getUserAuditLog(idVal);
          setIsUserDirty(false);
          setIsUserRoleDirty(false);
        }).catch(error => {
          ErrorService.addErrorLogEntry(currentUserInfo[FieldNames.userFields.nifcUsername], error);
        });
    } else {
      if(isUserDirty && isUserSuccess){
        //Add audit log entry - not stack so that even if this call fails the others will continue
        AuditLogService.addAuditLogEntry(
          user, idVal, FieldNames.auditLogJoinTypes.users, currentUserInfo[FieldNames.userFields.nifcUsername],
          ["User was modified"]).then(response => {
            getUserAuditLog(idVal);
            setIsUserDirty(false);
          }).catch(error => {
            ErrorService.addErrorLogEntry(currentUserInfo[FieldNames.userFields.nifcUsername], error);
          });
      } else if (isUserRoleDirty && isUserRoleSuccess){
        //Add audit log entry - not stack so that even if this call fails the others will continue
        AuditLogService.addAuditLogEntry(
          user, idVal, FieldNames.auditLogJoinTypes.users, currentUserInfo[FieldNames.userFields.nifcUsername],
          ["User Role was modified"]).then(response => {
            getUserAuditLog(idVal);
            setIsUserRoleDirty(false);
          }).catch(error => {
            ErrorService.addErrorLogEntry(currentUserInfo[FieldNames.userFields.nifcUsername], error);
          });
      }
    }
  }

  const addUserRoles = (allUserRoles, isUpdate, rolesToRemove, isUserDirty, isUserSuccess, isNewUser=false) => {
    if(allUserRoles && allUserRoles.length > 0 && (isUserRoleDirty || isNewUser)){
      UserService.addUserRoles(rootServiceUrl, nifcToken, allUserRoles, config).then(roles => {
        if(roles && roles.addResults && roles.addResults.length){
          let atLeastOneSuccess = false;
          let allSuccessfulNewRoles = getSuccessfulNewRoles(roles.addResults, allUserRoles);
          if(allSuccessfulNewRoles && allSuccessfulNewRoles.length){
            atLeastOneSuccess = true;
          }

          if(atLeastOneSuccess){
            //Once we successfully added new roles remove the old user roles before resetting the user roles for this user
            if(rolesToRemove && rolesToRemove.length > 0){
              UserService.deleteUsersRoles(rootServiceUrl, nifcToken, rolesToRemove, config).then((deleteResp) => {
                if(deleteResp && deleteResp.success){
                  if(allSuccessfulNewRoles && allSuccessfulNewRoles.length > 0){
                    setDbSavedUserRoles(allSuccessfulNewRoles);
                  } else {
                    setDbSavedUserRoles([]);
                  }
                }
              }).catch(error => {
                ErrorService.addErrorLogEntry(currentUserInfo[FieldNames.userFields.nifcUsername], error);
                return allSuccessfulNewRoles;
              });
            } else {
              if(allSuccessfulNewRoles && allSuccessfulNewRoles.length > 0){
                setDbSavedUserRoles(allSuccessfulNewRoles);
              } else {
                setDbSavedUserRoles([]);
              }
            }

            if(isUpdate){
              if(isUserSuccess && atLeastOneSuccess){
                setSnackbarMessage("User and User Roles updated successfully.");
              } else {
                if(isUserSuccess){
                  setSnackbarMessage("User updated successfully.");
                } else if(atLeastOneSuccess){
                  setSnackbarMessage("User Roles updated successfully.");
                }
              }
            } else {
              setSnackbarMessage("User and User Roles created successfully.");
              handleClearCreateUser();
            }
            setIsFailure(false);
            setSnackbarOpen(true);
            addAuditLog(isUserDirty, isUserSuccess, isUserRoleDirty, true);
            return allSuccessfulNewRoles;
          }
        } else {
          //Check the Nifc Username here to see if it's already been added
          //This is important in case the user saved but saving the user roles failed
          if(nifcUsername && isNewUser){
            setIsNifcAlreadyUsed(true);
          }
          setIsFailure(true);
          if(isUpdate){
            setSnackbarMessage("User updated successfully. User Roles update failed");
          } else {
            setSnackbarMessage("User created successfully. User Roles create failed");
          }
          setSnackbarOpen(true);
          addAuditLog(isUserDirty, isUserSuccess, isUserRoleDirty, false);
          return dbSavedUserRoles;
        }
      }).catch(error => {
        ErrorService.addErrorLogEntry(currentUserInfo[FieldNames.userFields.nifcUsername], error);
        return dbSavedUserRoles;
      });
    } else {
      addAuditLog(isUserDirty, isUserSuccess, false, false);
      if(!isUserSuccess){
        setIsFailure(true);
        if(isUpdate){
          setSnackbarMessage("User update failed!");
        } else {
          setSnackbarMessage("User create failed!");
        }
        setSnackbarOpen(true);
      } else {
        setIsFailure(false);
        if(isUpdate){
          setSnackbarMessage("User updated successfully.");
        } else {
          setSnackbarMessage("User created successfully.");
        }
        setSnackbarOpen(true);
      }
      return dbSavedUserRoles;
    }
  }

  const savingUserRoles = async(isUpdate, idVal, newUserID, dbSavedUserRoles, isUserDirty, isUserSuccess, isNewUser=false) => {
    let userRoles;
    if(isUpdate){
      userRoles = await getUserRoles(rootServiceUrl, config, idVal, newUserID, departmentSelectionUpdate, agencySelectionUpdate, regionSelectionUpdate, 
        unitSelectionUpdate, roleUpdate, selectedRoleUpdate, subUnitSelectionUpdate, tribeSelectionUpdate)
    } else {
      userRoles = await getUserRoles(rootServiceUrl, config, idVal, newUserID, departmentSelection, agencySelection, regionSelection, 
        unitSelection, role, selectedRole, subUnitSelection, tribeSelection)
    }
    return addUserRoles(userRoles, isUpdate, dbSavedUserRoles, isUserDirty, isUserSuccess, isNewUser);
  }

  const handleSaveUpdateUser = async() => {
    const userInfo = getUpdatedUserInfo(userIDUpdate, uuidv4(), currentUserInfo, setModifiedBy, setModifiedOn, setCreatedBy, setCreatedOn, createdBy, 
    firstNameUpdate, lastNameUpdate, emailUpdate, nifcUsernameUpdate, userIsActiveUpdate, userIsAdminUpdate, userIsFuelsUpdate, 
    userIsPostFireUpdate, userDefaultAppUpdate, userObjectIDUpdate, isTribalUserUpdate, createdOn, idVal);

    let newUserID = userInfo[FieldNames.userFields.userID];

    // Only save user if a user field changed
    if(isUserDirty){
      UserService.updateUser(rootServiceUrl, nifcToken, userInfo, config).then(async response => {
        if(response && response.updateResults && response.updateResults[0].success){
          // Handle updating roles
          let newRoles = [];
          if(dbSavedUserRoles && dbSavedUserRoles.length > 0 && isUserRoleDirty){
            newRoles = await savingUserRoles(true, idVal, newUserID, dbSavedUserRoles, isUserDirty, response.updateResults[0].success);
          } else {
            newRoles = await savingUserRoles(true, idVal, newUserID, null, isUserDirty, response.updateResults[0].success);
          }

          if(currentUserInfo[FieldNames.userFields.id] === userInfo[FieldNames.userFields.id])
          {
            // Updating one's own profile
            // Handle updating Redux cache
            if(currentUserInfo[FieldNames.userFields.isPostFire] === FieldNames.yesNo.yes
               && userInfo[FieldNames.userFields.isPostFire] === FieldNames.yesNo.no)
            {
              // User that was PostFire is no longer PostFire
              resetPostFireCacheValuesToFuels(); 
              resetPostFireCustomFiltersToFuels(userInfo);
              // Note: The following is temporary until Projects exist for Fuels
              resetProjectCacheValues();
            }
  
            if(currentUserInfo[FieldNames.userFields.isFuels] === FieldNames.yesNo.yes
               && userInfo[FieldNames.userFields.isFuels] === FieldNames.yesNo.no)
            {
              // User that was Fuels is no longer Fuels
              resetFuelsCacheValuesToPostFire(); 
              resetFuelsCustomFiltersToPostFire(userInfo);
            }

            // Update profile state variables
            let combinedUserInfo = _.cloneDeep(userInfo);
            Object.keys(currentUserInfo).forEach(function(k) {
              // Fill in any missing properties that the admin page does not use/update
              if (!combinedUserInfo.hasOwnProperty(k)) combinedUserInfo[k] = currentUserInfo[k];
            });
            setCurrentUserInfo(combinedUserInfo);
            setCurrentUserRoleInfo(newRoles);
          }
          else // Updating another user
          {
            // Handle updating Redux cache
            if(selectedUserOriginal[FieldNames.userFields.isPostFire] && !userInfo[FieldNames.userFields.isPostFire])
            {
              // User that was PostFire is no longer PostFire
              resetPostFireCustomFiltersToFuels(userInfo);
            }
  
            if(selectedUserOriginal[FieldNames.userFields.isFuels] && !userInfo[FieldNames.userFields.isFuels])
            {
              // User that was Fuels is no longer Fuels
              resetFuelsCustomFiltersToPostFire(userInfo);
            }
          }
        }
      }).catch(error => {
        ErrorService.addErrorLogEntry(currentUserInfo[FieldNames.userFields.nifcUsername], error);
      });
    } else if(isUserRoleDirty){
      // Case where user roles have changed, but base user info has not
      if(dbSavedUserRoles && dbSavedUserRoles.length > 0){
        await savingUserRoles(true, idVal, newUserID, dbSavedUserRoles, isUserDirty, false);
      } else {
        await savingUserRoles(true, idVal, newUserID, null, isUserDirty, false);
      }
    }
  }

  const handleClearCreateUser = () => {
    setFirstName("");
    setLastName("");
    setEmail("");
    setNifcUsername("");
    setUserIsAdmin(FieldNames.yesNo.no);
    setIsTribalUser(FieldNames.yesNo.no);
    setUserIsFuels(FieldNames.yesNo.no);
    setUserIsPostFire(FieldNames.yesNo.no);
    setUserIsActive(FieldNames.yesNo.yes);
    setUserDefaultApp("");
    setRole(null);
    setSelectedRole("");
    setIsNifcAlreadyUsed(false);
    setUnitSelection([]);
    setRegionSelection([]);
    setTribeSelection({TribeFullName: ""});
    setSubUnitSelection([]);
    setAgencySelection({ Agency: "" })
    setDepartmentSelection({ Department: FieldNames.defaults.department })
    //setDepartmentSelection(null);
  }

  const handleClearUpdateUser = () => {
    setUserIDUpdate("");
    setIdVal(null);
    setUserObjectIDUpdate(null);
    setEmailUpdate("")
    setFirstNameUpdate("")
    setLastNameUpdate("")
    setNifcUsernameUpdate("")
    setUserIsAdminUpdate(FieldNames.yesNo.no);
    setUserIsFuelsUpdate(FieldNames.yesNo.no);
    setUserIsPostFireUpdate(FieldNames.yesNo.no);
    setUserDefaultAppUpdate("");
    setUserIsActiveUpdate(FieldNames.yesNo.no);
    setIsTribalUserUpdate(FieldNames.yesNo.no);
    setRoleUpdate(null);
    setSelectedRoleUpdate("");
    setNifcInputValue("");
    setUnitSelectionUpdate([]);
    setTribeSelectionUpdate({TribeFullName: ""});
    setSubUnitSelectionUpdate([]);
    setRegionSelectionUpdate([]);
    setAgencySelectionUpdate({ Agency: "" })
    setDepartmentSelectionUpdate({ Department: FieldNames.defaults.department })
  }

  return (
    <Dialog
      style={{ minWidth: 550 }}
      onClose={onClose}
      fullScreen
      TransitionComponent={Transition}
      open={open}
      className="dialog-modal"
      PaperProps={{ 
        style: {
          backgroundColor: ColorConstants.LightGrayAlien
        }
      }}>
      <AppBar sx={{ position: 'relative' }} style={{ backgroundColor: ColorConstants.BlueAlien }}>
        <Toolbar style={{ color: ColorConstants.WhiteAlien }}>
          <IconButton
            edge="start"
            onClick={onClose}
            aria-label="close"
          >
            <CloseIcon style={{ fill: ColorConstants.WhiteAlien }} />
          </IconButton>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
            Admin Maintenance
          </Typography>
        </Toolbar>
      </AppBar>
      <Grid container direction="row" style={{ backgroundColor: ColorConstants.LightGrayAlien }}>
        <Grid item size={6} lg={6} container >
        <Paper elevation={6} style={{ padding: 20, margin: 20 }}>
            <h2>Create New User</h2>
            <AdminUserAssignment isCreate={true} allUsers={allUsers} setUserID={null} setIsUserDirty={setIsUserDirty} userID={null} setFirstName={setFirstName} firstName={firstName}
              setLastName={setLastName} lastName={lastName} setEmail={setEmail} email={email} setNifcUsername={setNifcUsername} nifcUsername={nifcUsername} 
              userIsAdmin={userIsAdmin} setUserIsAdmin={setUserIsAdmin} userIsActive={userIsActive} setUserIsActive={setUserIsActive} userIsFuels={userIsFuels} 
              setUserIsFuels={setUserIsFuels} userIsPostFire={userIsPostFire} setUserIsPostFire={setUserIsPostFire} isTribalUser={isTribalUser} 
              setIsTribalUser={setIsTribalUser} setRole={setRole} setSelectedRole={setSelectedRole} setAgencySelection={setAgencySelection} isNifcAlreadyUsed={isNifcAlreadyUsed} 
              setIsNifcAlreadyUsed={setIsNifcAlreadyUsed} />
            <AdminDialogAssignment userInfo={currentUserInfo} role={role} setRole={setRole} selectedRole={selectedRole} setSelectedRole={setSelectedRole} 
              departmentSelection={departmentSelection} setDepartmentSelection={setDepartmentSelection} 
              agencySelection={agencySelection} setAgencySelection={setAgencySelection} regionSelection={regionSelection} 
              setRegionSelection={setRegionSelection} unitSelection={unitSelection} setUnitSelection={setUnitSelection}
              subUnitSelection={subUnitSelection} setSubUnitSelection={setSubUnitSelection} 
              currentlyUsedRegions={currentlyUsedRegions} setCurrentlyUsedRegions={setCurrentlyUsedRegions} 
              currentlyUsedUnits={currentlyUsedUnits} setCurrentlyUsedUnits={setCurrentlyUsedUnits}
              roleOptions={roleValues} isTribalUser={isTribalUser} setIsTribalUser={setIsTribalUser} tribeSelection={tribeSelection} setTribeSelection={setTribeSelection}
              setIsUserRoleDirty={setIsUserRoleDirty} />
            <DialogActions>
              <Button
                color="primary"
                variant="contained"
                disabled={isNifcAlreadyUsed}
                style={{marginTop: "10px", marginRight: '15px', width: 100}}
                onClick={handleSaveCreateUser}>Save
              </Button>
              <Button
                color="primary"
                variant="contained"
                style={{marginTop: "10px", marginLeft:"15px", marginRight: '15px', width: 100}}
                onClick={handleClearCreateUser}>Clear
              </Button>
            </DialogActions>
          </Paper>
        </Grid>
        <Grid item size={6} lg={6} container >
        <Paper elevation={6} style={{ padding: 20, margin: 20 }}>
            <h2>Update User</h2>
            <FormControl variant="standard" className={classes.formControl}>
              <ThemeProvider theme={theme}>
                <Grid item size={12} container direction="row" className="grid-item" style={{ alignItems: 'center' }}>
                  <Grid item size={6}>
                    <span>
                      <label>Select Existing User</label>
                    </span>
                  </Grid>
                  <Grid item size={6}>
                    <Autocomplete
                      disablePortal
                      id="combo-box-demo"
                      options={allUsers}
                      getOptionKey={(option) => `${option[FieldNames.featureFields.id]}-${option[FieldNames.userFields.nifcUsername]}`}
                      getOptionLabel={(option) => option[FieldNames.userFields.nifcUsername]}
                      onChange={(event, newValue) => {
                        if(newValue){
                          setSelectedUserOriginal(newValue);
                          setUserIDUpdate(newValue[FieldNames.userFields.userID]);
                          setIdVal(newValue[FieldNames.userFields.id]);
                          setUserObjectIDUpdate(newValue[FieldNames.userFields.objectID]);
                          setEmailUpdate(newValue[FieldNames.userFields.email])
                          setFirstNameUpdate(newValue[FieldNames.userFields.firstName])
                          setLastNameUpdate(newValue[FieldNames.userFields.lastName])
                          setNifcUsernameUpdate(newValue[FieldNames.userFields.nifcUsername])
                          setUserIsAdminUpdate(newValue[FieldNames.userFields.isAdmin]);
                          setUserIsFuelsUpdate(newValue[FieldNames.userFields.isFuels] ? newValue[FieldNames.userFields.isFuels] : FieldNames.yesNo.no);
                          setUserIsPostFireUpdate(newValue[FieldNames.userFields.isPostFire] ? newValue[FieldNames.userFields.isPostFire] : FieldNames.yesNo.no);
                          setUserDefaultAppUpdate(newValue[FieldNames.userFields.defaultApp]);
                          setUserIsActiveUpdate(newValue[FieldNames.userFields.isActive]);
                          setCreatedBy(newValue[FieldNames.userFields.createdBy]);
                          setCreatedOn(formatDateForDisplay(newValue[FieldNames.userFields.createdOnDate]));
                          setModifiedBy(newValue[FieldNames.userFields.lastModifiedBy]);
                          setModifiedOn(formatDateForDisplay(newValue[FieldNames.userFields.lastModifiedDate]));
                          getUserAuditLog(newValue[FieldNames.userFields.id]);
                          const isTribal = (newValue[FieldNames.userFields.isTribalUser] === FieldNames.yesNo.no || newValue[FieldNames.userFields.isTribalUser] === FieldNames.yesNo.yes) ? newValue[FieldNames.userFields.isTribalUser] : FieldNames.yesNo.no;
                          setIsTribalUserUpdate(isTribal);

                          UserService.getUserRoles(rootServiceUrl, newValue[FieldNames.userFields.id], config).then(userRoles => {
                            if(userRoles && userRoles.length > 0){
                              setDbSavedUserRoles(userRoles);
                              //They will all have the same role
                              const savedRole = {name: `${userRoles[0][FieldNames.userRoles.appRole]}`, code: `${userRoles[0][FieldNames.userRoles.appRole]}`}
                              setRoleUpdate(savedRole)
                              setSelectedRoleUpdate(`${userRoles[0][FieldNames.userRoles.appRole]}`);
                              setDepartmentSelectionUpdate({Department: userRoles[0][FieldNames.userRoles.department]});
                              setAgencySelectionUpdate({Agency: userRoles[0][FieldNames.userRoles.agency]});
                              let savedRegions = [];
                              let savedUnits = [];
                              let usedRegions = [];
                              let usedUnits = [];
                              let savedSubUnits = [];
                              let savedTribes;
                              userRoles.forEach(savedUserRole => {
                                if(savedUserRole){
                                  if(savedUserRole[FieldNames.userRoles.region]){
                                    const currentSavedRegions = savedRegions.map(x => x[FieldNames.userRoles.region]);
                                    if(currentSavedRegions && !currentSavedRegions.includes(savedUserRole[FieldNames.userRoles.region])){
                                      savedRegions.push({Region: `${ savedUserRole[FieldNames.userRoles.region] }`});
                                    }
                                  }

                                  if(savedUserRole[FieldNames.userRoles.unit]){
                                    const currentSavedUnits = savedUnits.map(x => x[FieldNames.userRoles.unit]);
                                    if(currentSavedUnits && !currentSavedUnits.includes(savedUserRole[FieldNames.userRoles.unit])){
                                      savedUnits.push({Unit: `${ savedUserRole[FieldNames.userRoles.unit] }`});
                                    }
                                  }

                                  if(savedUserRole[FieldNames.userRoles.tribe]){
                                    savedTribes = {TribeFullName: `${savedUserRole[FieldNames.userRoles.tribe]}`};
                                    if(savedUserRole[FieldNames.userRoles.unit]){
                                      usedUnits.push(savedUserRole[FieldNames.userRoles.unit]);
                                    }
                                  }

                                  if(savedUserRole[FieldNames.userRoles.region] && savedUserRole[FieldNames.userRoles.unit]){
                                    usedRegions.push(savedUserRole[FieldNames.userRoles.region]);
                                  }

                                  if(savedUserRole[FieldNames.userRoles.unit] && savedUserRole[FieldNames.userRoles.subUnit]){
                                    usedUnits.push(savedUserRole[FieldNames.userRoles.unit]);
                                  }

                                  if(savedUserRole[FieldNames.userRoles.subUnit]){
                                    savedSubUnits.push({ SubUnit: `${savedUserRole[FieldNames.userRoles.subUnit] }`});
                                  }
                                }
                              });
                              setRegionSelectionUpdate(savedRegions);
                              setUnitSelectionUpdate(savedUnits);
                              setSubUnitSelectionUpdate(savedSubUnits);
                              setTribeSelectionUpdate(savedTribes);
                              setCurrentlyUsedRegionsUpdate(usedRegions);
                              setCurrentlyUsedUnitsUpdate(usedUnits);
                            }
                            
                          })
                          let currentRole = roleValues.filter(x => x.code === newValue[FieldNames.userFields.approvalRole]);
                          if(currentRole && currentRole.length > 0){
                            setSelectedRoleUpdate(currentRole[0].name);
                            setRoleUpdate(currentRole[0]);
                          } else {
                            setSelectedRoleUpdate("");
                            setRoleUpdate(null);
                          }
                          
                        } else {
                          handleClearUpdateUser();
                          setRoleUpdate(null)
                        }
                      }}
                      inputValue={nifcInputValue}
                      onInputChange={(event, newInputValue) => {
                        if(event){
                          setNifcInputValue(newInputValue);
                        } else {
                          setNifcInputValue("");
                        }
                        
                      }}
                      fullWidth
                      renderInput={(params) => <TextField style={{ paddingLeft: '0px' }} {...params} />}
                    />
                  </Grid>
                </Grid>
              </ThemeProvider>
            </FormControl>
            <AdminUserAssignment isCreate={false} allUsers={allUsers} setUserID={setUserIDUpdate} setIsUserDirty={setIsUserDirty} userID={userIDUpdate} setFirstName={setFirstNameUpdate} firstName={firstNameUpdate}
              setLastName={setLastNameUpdate} lastName={lastNameUpdate} setEmail={setEmailUpdate} email={emailUpdate} setNifcUsername={setNifcUsernameUpdate} nifcUsername={nifcUsernameUpdate} 
              userIsAdmin={userIsAdminUpdate} setUserIsAdmin={setUserIsAdminUpdate} userIsActive={userIsActiveUpdate} setUserIsActive={setUserIsActiveUpdate} userIsFuels={userIsFuelsUpdate} 
              setUserIsFuels={setUserIsFuelsUpdate} userIsPostFire={userIsPostFireUpdate} setUserIsPostFire={setUserIsPostFireUpdate} isTribalUser={isTribalUserUpdate} 
              setIsTribalUser={setIsTribalUserUpdate} setRole={setRoleUpdate} setSelectedRole={setSelectedRoleUpdate} setAgencySelection={setAgencySelectionUpdate} isNifcAlreadyUsed={isNifcAlreadyUsed} 
              setIsNifcAlreadyUsed={setIsNifcAlreadyUsed} />
            <AdminDialogAssignment userInfo={currentUserInfo} role={roleUpdate} setRole={setRoleUpdate} selectedRole={selectedRoleUpdate} setSelectedRole={setSelectedRoleUpdate} 
              departmentSelection={departmentSelectionUpdate} setDepartmentSelection={setDepartmentSelectionUpdate} 
              agencySelection={agencySelectionUpdate} setAgencySelection={setAgencySelectionUpdate} regionSelection={regionSelectionUpdate} 
              setRegionSelection={setRegionSelectionUpdate} unitSelection={unitSelectionUpdate} setUnitSelection={setUnitSelectionUpdate} 
              subUnitSelection={subUnitSelectionUpdate} setSubUnitSelection={setSubUnitSelectionUpdate} 
              currentlyUsedRegions={currentlyUsedRegionsUpdate} setCurrentlyUsedRegions={setCurrentlyUsedRegionsUpdate} 
              currentlyUsedUnits={currentlyUsedUnitsUpdate} setCurrentlyUsedUnits={setCurrentlyUsedUnitsUpdate}
              roleOptions={roleValues} isTribalUser={isTribalUserUpdate} setIsTribalUser={setIsTribalUserUpdate} tribeSelection={tribeSelectionUpdate} 
              setTribeSelection={setTribeSelectionUpdate} setIsUserRoleDirty={setIsUserRoleDirty} />
            <DialogActions>
              <Button
                color="primary"
                variant="contained"
                style={{marginTop: "10px", marginRight: '15px', width: 100}}
                onClick={handleSaveUpdateUser}>Save
              </Button>
              <Button
                color="primary"
                variant="contained"
                style={{marginTop: "10px", marginLeft:"15px", marginRight: '15px', width: 100}}
                onClick={handleClearUpdateUser}>Clear
              </Button>
            </DialogActions>
            <Box alignItems="center" justifyContent="center" sx={{paddingTop: "10px !important", paddingBottom: "10px !important"}}>
              <Grid marginLeft={"2%"} container>
                  <Grid item md={5}>
                      <Typography fontWeight={"fontWeightBold"}>Created By:</Typography> {createdBy}
                  </Grid>
                  <Grid item md={5}>
                    <Typography fontWeight={"fontWeightBold"}>Last Modified By:</Typography> {modifiedBy}
                  </Grid>
              </Grid>
              <Grid marginLeft={"2%"} container>
                  <Grid item md={5}>
                   <Typography fontWeight={"fontWeightBold"}>Created On:</Typography> {createdOn}
                  </Grid>
                  <Grid item md={5}>
                    <Typography fontWeight={"fontWeightBold"}>Last Modified On:</Typography> {modifiedOn}
                  </Grid>
              </Grid>
            </Box>
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1a-content"
                id="panel1a-header"
              >
                <Typography>View History</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{height: "250px", width: "100%", minWidth: "10px"}}>
                  <DataGridPremium
                    rows={userHistoryRows}
                    columns={userHistoryCols}>
                  </DataGridPremium>
                </Box>
              </AccordionDetails>
            </Accordion>
          </Paper>
        </Grid>
      </Grid>
      <ThemeProvider theme={theme}>
      <Snackbar open={snackbarOpen} autoHideDuration={6000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        className={classes.snackBarStyle}
        onClose={() => {
          setSnackbarOpen(false);
          setSnackbarMessage("");
        }}>
          <Alert onClose={() => {
          setSnackbarOpen(false);
          setSnackbarMessage("");
          }} severity={isFailure ? "error" : "success"} sx={{ width: '100%' }}>
            {snackbarMessage}
          </Alert>
      </Snackbar>
      </ThemeProvider>
    </Dialog>
  )
  
}

AdminDialog.propTypes = {
  open: PropTypes.bool, 
  onClose: PropTypes.func,
}